import type { Resume, ResumeContact, ResumeFont, ResumeLocale, ResumeSectionKey } from '../../../types'

/**
 * Shared helpers for HTML preview components — keeps each template thin.
 */

export const DEFAULT_ACCENT = '#0ea5e9'

export const DEFAULT_SECTION_ORDER: ResumeSectionKey[] = [
  'summary',
  'experience',
  'projects',
  'skills',
  'education',
  'certifications',
  'languages',
  'customSections',
]

const SECTION_LABELS: Record<ResumeLocale, Record<ResumeSectionKey, string>> = {
  en: {
    summary: 'Summary',
    experience: 'Experience',
    projects: 'Projects',
    skills: 'Skills',
    education: 'Education',
    certifications: 'Certifications',
    languages: 'Languages',
    customSections: '',
  },
  es: {
    summary: 'Resumen',
    experience: 'Experiencia',
    projects: 'Proyectos',
    skills: 'Habilidades',
    education: 'Educación',
    certifications: 'Certificaciones',
    languages: 'Idiomas',
    customSections: '',
  },
  fr: {
    summary: 'Résumé',
    experience: 'Expérience',
    projects: 'Projets',
    skills: 'Compétences',
    education: 'Formation',
    certifications: 'Certifications',
    languages: 'Langues',
    customSections: '',
  },
  de: {
    summary: 'Profil',
    experience: 'Berufserfahrung',
    projects: 'Projekte',
    skills: 'Kenntnisse',
    education: 'Ausbildung',
    certifications: 'Zertifikate',
    languages: 'Sprachen',
    customSections: '',
  },
  pt: {
    summary: 'Resumo',
    experience: 'Experiência',
    projects: 'Projetos',
    skills: 'Competências',
    education: 'Educação',
    certifications: 'Certificações',
    languages: 'Idiomas',
    customSections: '',
  },
  it: {
    summary: 'Profilo',
    experience: 'Esperienza',
    projects: 'Progetti',
    skills: 'Competenze',
    education: 'Formazione',
    certifications: 'Certificazioni',
    languages: 'Lingue',
    customSections: '',
  },
  pl: {
    summary: 'Podsumowanie',
    experience: 'Doświadczenie',
    projects: 'Projekty',
    skills: 'Umiejętności',
    education: 'Edukacja',
    certifications: 'Certyfikaty',
    languages: 'Języki',
    customSections: '',
  },
}

export function getLocale(resume: Resume): ResumeLocale {
  return resume.contact?.locale ?? 'en'
}

export function getAccent(resume: Resume): string {
  return resume.contact?.accentColor ?? DEFAULT_ACCENT
}

export function getSectionOrder(resume: Resume): ResumeSectionKey[] {
  const stored = resume.contact?.sectionOrder
  if (!stored || !Array.isArray(stored) || stored.length === 0) return DEFAULT_SECTION_ORDER
  // Append any missing keys (forward-compatible if new sections are added later).
  const missing = DEFAULT_SECTION_ORDER.filter((k) => !stored.includes(k))
  return [...stored.filter((k) => DEFAULT_SECTION_ORDER.includes(k)), ...missing]
}

export function labelFor(resume: Resume, key: ResumeSectionKey): string {
  return SECTION_LABELS[getLocale(resume)][key]
}

/**
 * Font family resolution for both PDF (react-pdf built-in + Google Fonts) and HTML preview.
 *
 * Built-in families (Helvetica, Times-Roman, Courier) ship with bold/italic variants.
 * Lato and PT Serif are registered via Google Fonts TTF URLs (jsdelivr CDN).
 */
import { Font } from '@react-pdf/renderer'

const GOOGLE_FONTS_BASE = 'https://cdn.jsdelivr.net/gh/google/fonts@main/ofl'

let googleFontsRegistered = false
function ensureGoogleFontsRegistered() {
  if (googleFontsRegistered) return
  googleFontsRegistered = true
  Font.register({
    family: 'Lato',
    fonts: [
      { src: `${GOOGLE_FONTS_BASE}/lato/Lato-Regular.ttf` },
      { src: `${GOOGLE_FONTS_BASE}/lato/Lato-Bold.ttf`, fontWeight: 'bold' },
      { src: `${GOOGLE_FONTS_BASE}/lato/Lato-Italic.ttf`, fontStyle: 'italic' },
      { src: `${GOOGLE_FONTS_BASE}/lato/Lato-BoldItalic.ttf`, fontWeight: 'bold', fontStyle: 'italic' },
    ],
  })
  Font.register({
    family: 'PT Serif',
    fonts: [
      { src: `${GOOGLE_FONTS_BASE}/ptserif/PT_Serif-Web-Regular.ttf` },
      { src: `${GOOGLE_FONTS_BASE}/ptserif/PT_Serif-Web-Bold.ttf`, fontWeight: 'bold' },
      { src: `${GOOGLE_FONTS_BASE}/ptserif/PT_Serif-Web-Italic.ttf`, fontStyle: 'italic' },
      { src: `${GOOGLE_FONTS_BASE}/ptserif/PT_Serif-Web-BoldItalic.ttf`, fontWeight: 'bold', fontStyle: 'italic' },
    ],
  })
}

export type PdfFontFamily = {
  regular: string
  bold: string
  italic: string
  boldItalic: string
}

// For react-pdf, custom registered fonts use a single family name and react-pdf
// resolves the bold/italic variants via fontWeight/fontStyle on the Text style.
// To keep our templates simple (they pass full font names as fontFamily), we
// register the bold/italic variants under separate family names too. This way
// `fontFamily: 'Lato-Bold'` works the same as `fontFamily: 'Helvetica-Bold'`.
function registerAliasFamilies() {
  Font.register({ family: 'Lato-Bold', src: `${GOOGLE_FONTS_BASE}/lato/Lato-Bold.ttf` })
  Font.register({ family: 'Lato-Italic', src: `${GOOGLE_FONTS_BASE}/lato/Lato-Italic.ttf` })
  Font.register({ family: 'Lato-BoldItalic', src: `${GOOGLE_FONTS_BASE}/lato/Lato-BoldItalic.ttf` })
  Font.register({ family: 'PT Serif-Bold', src: `${GOOGLE_FONTS_BASE}/ptserif/PT_Serif-Web-Bold.ttf` })
  Font.register({ family: 'PT Serif-Italic', src: `${GOOGLE_FONTS_BASE}/ptserif/PT_Serif-Web-Italic.ttf` })
  Font.register({ family: 'PT Serif-BoldItalic', src: `${GOOGLE_FONTS_BASE}/ptserif/PT_Serif-Web-BoldItalic.ttf` })
}

const PDF_FONT_FAMILIES: Record<ResumeFont, PdfFontFamily> = {
  sans: {
    regular: 'Helvetica',
    bold: 'Helvetica-Bold',
    italic: 'Helvetica-Oblique',
    boldItalic: 'Helvetica-BoldOblique',
  },
  serif: {
    regular: 'Times-Roman',
    bold: 'Times-Bold',
    italic: 'Times-Italic',
    boldItalic: 'Times-BoldItalic',
  },
  mono: {
    regular: 'Courier',
    bold: 'Courier-Bold',
    italic: 'Courier-Oblique',
    boldItalic: 'Courier-BoldOblique',
  },
  lato: {
    regular: 'Lato',
    bold: 'Lato-Bold',
    italic: 'Lato-Italic',
    boldItalic: 'Lato-BoldItalic',
  },
  ptserif: {
    regular: 'PT Serif',
    bold: 'PT Serif-Bold',
    italic: 'PT Serif-Italic',
    boldItalic: 'PT Serif-BoldItalic',
  },
}

const PREVIEW_FONT_FAMILIES: Record<ResumeFont, string> = {
  sans: 'Helvetica, Arial, sans-serif',
  serif: '"Times New Roman", Times, serif',
  mono: '"Courier New", Courier, monospace',
  lato: '"Lato", system-ui, sans-serif',
  ptserif: '"PT Serif", Georgia, serif',
}

export function getFont(resume: Resume, fallback: ResumeFont = 'sans'): ResumeFont {
  return resume.contact?.font ?? fallback
}

export function pdfFontFamily(resume: Resume, fallback: ResumeFont = 'sans'): PdfFontFamily {
  const choice = getFont(resume, fallback)
  if (choice === 'lato' || choice === 'ptserif') {
    ensureGoogleFontsRegistered()
    registerAliasFamilies()
  }
  return PDF_FONT_FAMILIES[choice]
}

export function previewFontFamily(resume: Resume, fallback: ResumeFont = 'sans'): string {
  return PREVIEW_FONT_FAMILIES[getFont(resume, fallback)]
}

/**
 * ATS-friendly bullet prefix. Plain ASCII so resume parsers can extract bullets
 * without choking on the Unicode bullet glyph.
 */
export const BULLET = '- '

/**
 * Strip protocol from a URL for visual display (the underlying href keeps it).
 */
export function displayUrl(url: string): string {
  return url.replace(/^https?:\/\//i, '').replace(/\/$/, '')
}

export function joinDateRange(start?: string, end?: string): string {
  if (start && end) return `${start} – ${end}`
  if (start) return start
  if (end) return end
  return ''
}

export function contactInline(contact: ResumeContact): string[] {
  const out: string[] = []
  if (contact.email) out.push(contact.email)
  if (contact.phone) out.push(contact.phone)
  if (contact.location) out.push(contact.location)
  if (contact.website) out.push(displayUrl(contact.website))
  if (contact.linkedin) out.push(displayUrl(contact.linkedin))
  if (contact.github) out.push(displayUrl(contact.github))
  return out
}

export function hasAnyContent(resume: Resume): boolean {
  const c = resume.contact
  return Boolean(
    (c.fullName && c.fullName.trim()) ||
    (resume.summary && resume.summary.trim()) ||
    resume.experience.length ||
    resume.education.length ||
    resume.skills.length ||
    resume.projects.length ||
    resume.certifications.length ||
    resume.languages.length ||
    resume.customSections.length
  )
}

export function pdfMetadata(resume: Resume): { title: string; author: string; subject: string } {
  const author = resume.contact?.fullName?.trim() || 'Resume'
  return {
    title: resume.title || `${author} - Resume`,
    author,
    subject: 'Resume',
  }
}

